import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon';
import { HBox, VBox } from '../layout';
import { intl } from '../../lib';

/**
 * The properties for the Offline Handler component.
 *
 * @author Patrick Lantinger
 */
export type OfflineHandlerProps = {
  /**
   When the connection is reestablished should the site be reloaded.
   */
  refresh: boolean; //refresh the site after the connection is regained
};

/**
 * @param refresh if the site should be reloaded after the connection was reestablished
 *
 * @author Patrick Lanzinger
 */
export const OfflineHandler = ({
  refresh = false
}: OfflineHandlerProps): JSX.Element => {
  const [online, setOnline] = useState(true);

  const onlineListener = useCallback(() => {
    console.log('The application has an active internet connection');
    if (!online && refresh) {
      window.location.reload();
    }
    setOnline(true);
  }, [online, refresh]);

  const offlineListener = useCallback(() => {
    console.log('The application has lost the internet connection');
    setOnline(false);
  }, []);

  useEffect(() => {
    window.addEventListener('online', onlineListener);
    window.addEventListener('offline', offlineListener);

    return () => {
      window.removeEventListener('online', onlineListener);
      window.removeEventListener('offline', offlineListener);
    };
  }, [offlineListener, onlineListener]);
  return (
    <div
      className="ehs-offline-handler"
      style={{ display: !online ? '' : 'none' }}
      data-testid="offline-handler"
      aria-live="polite"
    >
      <VBox className="offline-handler-child-background">
        <HBox>
          <Icon
            iconName={'error'}
            aria-label={intl('common.nointernetconnection')}
          />
          <span aria-hidden>{intl('common.nointernetconnection')}</span>
        </HBox>
      </VBox>
    </div>
  );
};
